/**
 * Signed-in user's account: identity + change own Cognito password.
 */
import { useEffect, useState } from "react";
import { fetchAuthSession, updatePassword } from "aws-amplify/auth";
import { usePortalTour } from "../components/PortalTour";
import InfoTip from "../components/InfoTip";
import { getImpersonationClaims } from "../impersonationSession";

const TIPS = {
  identity: "Email and username from your signed-in Cognito account.",
  tour: "Replay the short guided walkthrough of the main portal areas.",
  currentPassword: "Your current sign-in password. Required to set a new one.",
  newPassword:
    "At least 8 characters, with uppercase, lowercase, and a number. Must differ from the current password.",
  confirmPassword: "Re-enter the new password to confirm it matches.",
};

const panelStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
  background: "#fff",
  maxWidth: 480,
};
const fieldStyle = { marginBottom: 14 };
const labelStyle = {
  display: "block",
  fontWeight: 600,
  color: "#374151",
  fontSize: 13,
  marginBottom: 6,
};
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  fontSize: 14,
};
const hintStyle = { fontSize: 12, color: "#6b7280", marginTop: 4, marginBottom: 0 };

const bannerBase = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "14px 16px",
  borderRadius: 10,
  marginBottom: 16,
  border: "2px solid",
};
const successBannerStyle = {
  ...bannerBase,
  background: "#dcfce7",
  borderColor: "#16a34a",
  color: "#14532d",
};
const errorBannerStyle = {
  ...bannerBase,
  background: "#fee2e2",
  borderColor: "#dc2626",
  color: "#7f1d1d",
};
const bannerTitleStyle = { fontSize: 16, fontWeight: 700, margin: 0, lineHeight: 1.3 };
const bannerBodyStyle = { fontSize: 14, margin: "4px 0 0", fontWeight: 500, lineHeight: 1.4 };
const bannerIconStyle = { fontSize: 22, lineHeight: 1, fontWeight: 700 };

function StatusBanner({ kind, title, message }) {
  if (!message) return null;
  const isSuccess = kind === "success";
  return (
    <div
      role={isSuccess ? "status" : "alert"}
      aria-live={isSuccess ? "polite" : "assertive"}
      style={isSuccess ? successBannerStyle : errorBannerStyle}
    >
      <div style={bannerIconStyle} aria-hidden="true">
        {isSuccess ? "✓" : "!"}
      </div>
      <div>
        <p style={bannerTitleStyle}>{title}</p>
        <p style={bannerBodyStyle}>{message}</p>
      </div>
    </div>
  );
}

function passwordMeetsPolicy(password) {
  return (
    password.length >= 8 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password)
  );
}

function mapPasswordError(err) {
  const name = err?.name || "";
  const message = String(err?.message || err || "");
  if (name === "NotAuthorizedException" || /incorrect.*password/i.test(message)) {
    return "Current password is incorrect.";
  }
  if (name === "InvalidPasswordException" || /password.*policy|did not conform/i.test(message)) {
    return "New password does not meet the password requirements.";
  }
  if (name === "LimitExceededException" || /limit exceeded/i.test(message)) {
    return "Too many attempts. Please try again later.";
  }
  if (name === "PasswordResetRequiredException") {
    return "A password reset is required. Sign out and use Forgot password on the sign-in screen.";
  }
  return "Could not change password. Please try again.";
}

export default function Account() {
  const { startTour, tourRunning } = usePortalTour();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    document.title = "Account";
  }, []);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const imp = getImpersonationClaims();
        if (imp) {
          if (!mounted) return;
          setImpersonating(true);
          setEmail(imp.email || "");
          setUsername(
            imp.impersonator_username || imp["cognito:username"] || imp.sub || ""
          );
          return;
        }
        if (mounted) setImpersonating(false);
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload || {};
        if (!mounted) return;
        setEmail(payload.email || "");
        setUsername(payload["cognito:username"] || payload.sub || "");
      } catch (err) {
        if (mounted) setError("Could not load account details.");
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Fill in all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("New password must be different from the current password.");
      return;
    }
    if (!passwordMeetsPolicy(newPassword)) {
      setError("New password must be at least 8 characters and include uppercase, lowercase, and a number.");
      return;
    }

    setSubmitting(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess("Password updated.");
    } catch (err) {
      setError(mapPasswordError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 16 }}>Account</h2>

      {error ? (
        <StatusBanner kind="error" title="Password not updated" message={error} />
      ) : null}
      {success ? (
        <StatusBanner kind="success" title="Password updated" message={success} />
      ) : null}

      <div style={{ ...panelStyle, marginBottom: 20 }}>
        <h3
          style={{
            margin: "0 0 12px",
            fontSize: 16,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Signed in as
          <InfoTip text={TIPS.identity} />
        </h3>
        <div style={{ marginBottom: 8, fontSize: 14 }}>
          <span style={{ fontWeight: 600, color: "#374151", marginRight: 8 }}>Email:</span>
          <span>{email || "—"}</span>
        </div>
        {username ? (
          <div style={{ fontSize: 14 }}>
            <span style={{ fontWeight: 600, color: "#374151", marginRight: 8 }}>Username:</span>
            <span>{username}</span>
          </div>
        ) : null}
      </div>

      <div style={{ ...panelStyle, marginBottom: 20 }}>
        <h3
          style={{
            margin: "0 0 8px",
            fontSize: 16,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Portal tour
          <InfoTip text={TIPS.tour} />
        </h3>
        <p style={{ ...hintStyle, marginBottom: 12 }}>
          A short walkthrough of Dashboard, Slides, Reports, Organization, notices, and Account.
        </p>
        <button
          type="button"
          onClick={() => startTour()}
          disabled={tourRunning}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "1px solid #1d4ed8",
            background: tourRunning ? "#93c5fd" : "#2563eb",
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            cursor: tourRunning ? "default" : "pointer",
          }}
        >
          {tourRunning ? "Tour in progress…" : "Take a quick tour"}
        </button>
      </div>

      <form onSubmit={onSubmit} style={panelStyle} autoComplete="off">
        <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>Change password</h3>
        {impersonating ? (
          <p style={{ ...hintStyle, marginBottom: 0 }}>
            Password changes are unavailable during a Super Admin impersonation
            session. End the session and sign in normally to change your password.
          </p>
        ) : (
          <>
        <p style={{ ...hintStyle, marginBottom: 14 }}>
          At least 8 characters, with uppercase, lowercase, and a number.
        </p>

        <div style={fieldStyle}>
          <label htmlFor="current-password" style={labelStyle}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Current password
              <InfoTip text={TIPS.currentPassword} />
            </span>
          </label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={fieldStyle}>
          <label htmlFor="new-password" style={labelStyle}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              New password
              <InfoTip text={TIPS.newPassword} />
            </span>
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={fieldStyle}>
          <label htmlFor="confirm-password" style={labelStyle}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Confirm new password
              <InfoTip text={TIPS.confirmPassword} />
            </span>
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={inputStyle}
          />
        </div>

        {error ? (
          <StatusBanner kind="error" title="Password not updated" message={error} />
        ) : null}
        {success ? (
          <StatusBanner kind="success" title="Password updated" message={success} />
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "1px solid #1d4ed8",
            background: submitting ? "#93c5fd" : "#2563eb",
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            cursor: submitting ? "default" : "pointer",
          }}
        >
          {submitting ? "Updating…" : "Update password"}
        </button>
          </>
        )}
      </form>
    </div>
  );
}
